import { TripAnnotationNoAudioReason } from '@backoffice-frontend/graphql'
import type { Theme } from '@emotion/react'
import { PlayIcon } from '@moia-dev/pace-icons'
import { Button, LoadingIndicator, Text } from '@moia-dev/pace-web'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import type { AnnotationAudioUnavailableReason } from './annotation-audio'
import { useTripAnnotationAudio } from './useTripAnnotationAudio'

const rowCss = (theme: Theme) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.space.Space2,
    minHeight: theme.space.Space8,
  }) as const

// Switch rather than a map: i18next-scanner only keeps keys that reach `t()` as
// string literals.
const unavailableLabel = (
  t: TFunction,
  reason: AnnotationAudioUnavailableReason,
): string => {
  switch (reason) {
    case TripAnnotationNoAudioReason.NotStored:
      return t('mapAnnotations_audio_reason_notStored')
    case TripAnnotationNoAudioReason.ClipMissing:
      return t('mapAnnotations_audio_reason_clipMissing')
    case TripAnnotationNoAudioReason.ForeignBucket:
      return t('mapAnnotations_audio_reason_foreignBucket')
    case TripAnnotationNoAudioReason.UnreadableUri:
      return t('mapAnnotations_audio_reason_unreadableUri')
    default:
      return t('mapAnnotations_audio_reason_unknown')
  }
}

// Not the `autoPlay` attribute: map popups stay mounted after they close, with
// their element detached from the document, so a fetch that resolves late would
// start playback nobody can see or pause. Playing only while connected leaves
// the controls ready for when the popup is opened again.
const playWhenVisible = (audio: HTMLAudioElement | null) => {
  if (audio?.isConnected) audio.play().catch(() => {})
}

type TripAnnotationAudioPlayerProps = {
  annotationId: string
}

/**
 * Plays a trip annotation's recording. Shows only a Play button until clicked,
 * then fetches a fresh presigned URL (see `useTripAnnotationAudio`) and plays it
 * in a native `<audio controls>` element, or says why there is nothing to play.
 *
 * Rendered for every note, not only those with an `audioS3Uri`: a null URI can
 * mean the recording is still being built, which only the URL query can tell.
 */
export const TripAnnotationAudioPlayer = ({
  annotationId,
}: TripAnnotationAudioPlayerProps) => {
  const { t } = useTranslation()
  const { state, play, onPlaybackError } = useTripAnnotationAudio(annotationId)

  const retryButton = (
    <Button
      size="compact"
      variant="secondary"
      label={t('mapAnnotations_audio_retry')}
      onClick={play}
    />
  )

  switch (state.status) {
    case 'idle':
      return (
        <div css={rowCss}>
          <Button
            size="compact"
            variant="secondary"
            icon={<PlayIcon />}
            label={t('mapAnnotations_audio_playLabel')}
            onClick={play}
          />
        </div>
      )
    case 'loading':
      return (
        <div css={rowCss}>
          <LoadingIndicator size={16} />
          <Text size="sm" color="reduced">
            {t('mapAnnotations_audio_loadingLabel')}
          </Text>
        </div>
      )
    case 'ready':
      return (
        // biome-ignore lint/a11y/useMediaCaption: driver recordings have no caption track; the transcript is shown beside the player.
        <audio
          controls
          ref={playWhenVisible}
          src={state.url}
          onError={onPlaybackError}
          aria-label={t('mapAnnotations_audio_playerLabel')}
          css={{ width: '100%' }}
        />
      )
    case 'processing':
      return state.exhausted ? (
        <div css={rowCss}>
          <Text size="sm" color="reduced">
            {t('mapAnnotations_audio_processingStalled')}
          </Text>
          {retryButton}
        </div>
      ) : (
        <div css={rowCss}>
          <LoadingIndicator size={16} />
          <Text size="sm" color="reduced">
            {t('mapAnnotations_audio_processing')}
          </Text>
        </div>
      )
    case 'unavailable':
      return (
        <div css={rowCss}>
          <Text size="sm" color="reduced">
            {unavailableLabel(t, state.reason)}
          </Text>
        </div>
      )
    case 'error':
      return (
        <div css={rowCss}>
          <Text size="sm" color="reduced">
            {t('mapAnnotations_audio_error')}
          </Text>
          {retryButton}
        </div>
      )
  }
}
